// @ts-check

import { useQueryClient } from "@tanstack/react-query";
import { SSE_ENTITY } from "economos-shared/sse";
import { useEffect, useRef } from "react";
import { crewStore, userStore } from "@/mobx/rootStore";
import { applySseEvent } from "@/utils/sse/applySseEvent";

/**
 * Mantiene una única conexión SSE (EventSource) por crew mientras la sesión
 * está autenticada. Aplica cada evento a la caché de TanStack Query sin refetch.
 * En reconexión (tras error) invalida las queries de dinero para re-sincronizar
 * lo que se haya perdido con la conexión caída.
 */
export function useEventsConnection() {
	const queryClient = useQueryClient();
	const crewId = crewStore.getSelectedCrew("id");
	const isAuthenticated = userStore.isAuthenticated;
	const hadConnectionErrorRef = useRef(false);

	useEffect(() => {
		if (!crewId || !isAuthenticated) return undefined;

		const url = `${import.meta.env.VITE_API_URL}/events?crewId=${encodeURIComponent(crewId)}`;
		const eventSource = new EventSource(url, { withCredentials: true });

		function handleConnected() {
			if (hadConnectionErrorRef.current) {
				hadConnectionErrorRef.current = false;

				queryClient.invalidateQueries({ queryKey: ["economosMovement"] });
				queryClient.invalidateQueries({ queryKey: ["economosList"] });
				queryClient.invalidateQueries({ queryKey: ["economosMetrics"] });
				queryClient.invalidateQueries({ queryKey: ["houseTask"] });
				queryClient.invalidateQueries({ queryKey: ["houseItem"] });
				queryClient.invalidateQueries({ queryKey: ["houseItemCategory"] });
			}
		}

		function handleError() {
			// EventSource reintenta automáticamente; marcamos para re-sincronizar al volver.
			hadConnectionErrorRef.current = true;
		}

		/**
		 * @param {MessageEvent} messageEvent
		 */
		function handleEvent(messageEvent) {
			try {
				applySseEvent(queryClient, JSON.parse(messageEvent.data));
			} catch (error) {
				console.log("[sse] Evento inválido", error);
			}
		}

		// "open" es el evento estándar: se emite también en cada reconexión automática.
		eventSource.addEventListener("open", handleConnected);
		eventSource.addEventListener("error", handleError);
		eventSource.addEventListener(SSE_ENTITY.ECONOMOS_MOVEMENT, handleEvent);
		eventSource.addEventListener(SSE_ENTITY.ECONOMOS_LIST, handleEvent);
		eventSource.addEventListener(SSE_ENTITY.HOUSE_TASK, handleEvent);
		eventSource.addEventListener(SSE_ENTITY.HOUSE_ITEM, handleEvent);
		eventSource.addEventListener(SSE_ENTITY.HOUSE_ITEM_CATEGORY, handleEvent);

		return () => {
			eventSource.close();
		};
	}, [crewId, isAuthenticated, queryClient]);
}
